import React, { useState } from 'react';
import { useAuth } from '../AuthContext';
import './AuthModal.css';

export default function AuthModal({ onSuccess, onClose, reason, initialMode = 'signin' }) {
  const { signIn, signUp, resetPassword } = useAuth();
  const [mode, setMode]           = useState(initialMode); // 'signin' | 'signup' | 'forgot'
  const [email, setEmail]         = useState('');
  const [password, setPassword]   = useState('');
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState(null);
  const [checkEmail, setCheckEmail] = useState(false);

  const switchMode = (m) => { setMode(m); setError(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const { error } = await resetPassword(email);
        if (error) throw error;
        setCheckEmail(true);
      } else if (mode === 'signup') {
        const { data, error } = await signUp(email, password);
        if (error) throw error;
        if (data.session) {
          onSuccess();
        } else {
          setCheckEmail(true);
        }
      } else {
        const { data, error } = await signIn(email, password);
        if (error) throw error;
        onSuccess(data.session);
      }
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // ── Check email state (signup confirmation or password reset) ──────────────
  if (checkEmail) {
    const isReset = mode === 'forgot';
    return (
      <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="auth-modal">
          <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>
          <div className="auth-check-email">
            <div className="auth-check-icon">📬</div>
            <h2>{isReset ? 'Check your email' : 'Confirm your email'}</h2>
            <p>
              {isReset
                ? <>We sent a password reset link to <strong>{email}</strong>. Click it to set a new password.</>
                : <>We sent a confirmation link to <strong>{email}</strong>. Click it to activate your account, then come back and sign in.</>
              }
            </p>
            <button className="auth-switch-btn" onClick={() => { setCheckEmail(false); switchMode('signin'); }}>
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ── Main modal ────────────────────────────────────────────────────────────
  return (
    <div className="auth-overlay" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="auth-modal">
        <button className="auth-close" onClick={onClose} aria-label="Close">✕</button>

        {/* Forgot password view */}
        {mode === 'forgot' ? (
          <>
            <div className="auth-header">
              <h2>Reset password</h2>
              <p className="auth-reason">Enter your email and we'll send you a reset link.</p>
            </div>
            <form className="auth-form" onSubmit={handleSubmit}>
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />
              {error && <p className="auth-error">{error}</p>}
              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? <span className="auth-spinner"></span> : 'Send reset link'}
              </button>
            </form>
            <p className="auth-footer-note">
              <button className="auth-link" onClick={() => switchMode('signin')}>Back to sign in</button>
            </p>
          </>
        ) : (
          <>
            {/* Sign in / Sign up view */}
            <div className="auth-header">
              <h2>{mode === 'signin' ? 'Sign in' : 'Create account'}</h2>
              {reason && <p className="auth-reason">{reason}</p>}
            </div>

            <div className="auth-tabs">
              <button className={`auth-tab ${mode === 'signin' ? 'active' : ''}`} onClick={() => switchMode('signin')}>Sign in</button>
              <button className={`auth-tab ${mode === 'signup' ? 'active' : ''}`} onClick={() => switchMode('signup')}>Create account</button>
            </div>

            <form className="auth-form" onSubmit={handleSubmit}>
              <label className="auth-label">Email</label>
              <input
                className="auth-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                autoFocus
              />

              <label className="auth-label">Password</label>
              <input
                className="auth-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 6 characters' : '••••••••'}
                required
                minLength={6}
              />

              {mode === 'signin' && (
                <button
                  type="button"
                  className="auth-forgot-link"
                  onClick={() => switchMode('forgot')}
                >
                  Forgot password?
                </button>
              )}

              {error && <p className="auth-error">{error}</p>}

              <button className="auth-submit" type="submit" disabled={loading}>
                {loading ? <span className="auth-spinner"></span> : (mode === 'signin' ? 'Sign in' : 'Create account')}
              </button>
            </form>

            <p className="auth-footer-note">
              {mode === 'signin' ? (
                <>No account? <button className="auth-link" onClick={() => switchMode('signup')}>Create one</button></>
              ) : (
                <>Already have an account? <button className="auth-link" onClick={() => switchMode('signin')}>Sign in</button></>
              )}
            </p>

            <p className="auth-legal">
              By continuing you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer">Terms</a> and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>.
            </p>
          </>
        )}
      </div>
    </div>
  );
}
